import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectLogframeApi, projectActivitiesApi } from '../../../api/projectWorkspace';
import { ApiError } from '../../../api/client';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { CommentThread } from '../../../components/CommentThread';
import { usePermission } from '../../../auth/AuthContext';
import { Project, ProjectOutcome, ProjectIndicator, ProjectActivity } from '../../../types/projects';

// No code fields anywhere below - Outcome/Output/Indicator/Activity
// numbers are generated by the server. The user only ever supplies
// the statement/name text.
const outcomeSchema = z.object({ statement: z.string().min(3) });
const outputSchema = z.object({ statement: z.string().min(3) });

const indicatorSchema = z.object({
  name: z.string().min(3),
  baselineValue: z.string().optional(),
  targetValue: z.string().optional(),
  targetMen: z.string().optional(),
  targetWomen: z.string().optional(),
  targetBoys: z.string().optional(),
  targetGirls: z.string().optional(),
  targetTotal: z.string().optional(),
  unitOfMeasurement: z.string().optional(),
  meansOfVerification: z.string().optional(),
});

function toNullableNumber(value: string | undefined): number | undefined {
  if (value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

const activitySchema = z.object({ name: z.string().min(3) });

/** Used everywhere a delete can be blocked by dependent records - Section 7's exact message. */
function deleteErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return 'Failed to delete.';
}

export function ProjectLogframeTab({ project }: { project: Project }) {
  const canManage = usePermission('project_logframe.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [outcomeModalOpen, setOutcomeModalOpen] = useState(false);

  const outcomesQuery = useQuery({ queryKey: ['project-outcomes', project.id], queryFn: () => projectLogframeApi.listOutcomes(project.id) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<z.infer<typeof outcomeSchema>>({ resolver: zodResolver(outcomeSchema) });
  const createOutcomeMutation = useMutation({
    mutationFn: (values: z.infer<typeof outcomeSchema>) => projectLogframeApi.createOutcome(project.id, values),
    onSuccess: () => { showSuccess('Outcome added.'); queryClient.invalidateQueries({ queryKey: ['project-outcomes', project.id] }); setOutcomeModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add outcome.'),
  });

  const deleteOutcomeMutation = useMutation({
    mutationFn: (id: number) => projectLogframeApi.deactivateOutcome(id),
    onSuccess: () => { showSuccess('Outcome deleted.'); queryClient.invalidateQueries({ queryKey: ['project-outcomes', project.id] }); },
    onError: (err) => showError(deleteErrorMessage(err)),
  });

  function handleDeleteOutcome(id: number, label: string) {
    if (window.confirm(`Are you sure you want to delete ${label}? This action cannot be undone.`)) {
      deleteOutcomeMutation.mutate(id);
    }
  }

  return (
    <>
      <div className="page-header"><h2>Logical Framework</h2>{canManage && <button className="btn-accent-1" onClick={() => setOutcomeModalOpen(true)}>+ Add Outcome</button>}</div>

      {outcomesQuery.data && outcomesQuery.data.data.length === 0 && <div className="card"><p className="muted">No outcomes defined yet.</p></div>}

      {outcomesQuery.data?.data.map((outcome, idx) => (
        <OutcomeCard
          key={outcome.id}
          outcome={outcome}
          projectId={project.id}
          canManage={canManage}
          colorIndex={idx}
          onDelete={() => handleDeleteOutcome(outcome.id, `Outcome ${outcome.outcome_code}`)}
        />
      ))}

      {outcomeModalOpen && (
        <Modal title="Add Outcome" onClose={() => setOutcomeModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createOutcomeMutation.mutate(values))}>
            <label>Outcome Statement<textarea rows={2} {...register('statement')} />{errors.statement && <span className="field-error">{errors.statement.message}</span>}</label>
            <p className="muted">The outcome number is assigned automatically.</p>
            <div className="form-actions"><button type="submit" disabled={createOutcomeMutation.isPending}>{createOutcomeMutation.isPending ? 'Saving…' : 'Add'}</button></div>
          </form>
        </Modal>
      )}
    </>
  );
}

const OUTCOME_ACCENTS = ['outcome-accent-1', 'outcome-accent-2', 'outcome-accent-3', 'outcome-accent-4', 'outcome-accent-5'];

function OutcomeCard({
  outcome, projectId, canManage, colorIndex, onDelete,
}: {
  outcome: ProjectOutcome; projectId: number; canManage: boolean; colorIndex: number; onDelete: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [outputModalOpen, setOutputModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const outputsQuery = useQuery({ queryKey: ['project-outputs', outcome.id], queryFn: () => projectLogframeApi.listOutputs(outcome.id) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<z.infer<typeof outputSchema>>({ resolver: zodResolver(outputSchema) });
  const createOutputMutation = useMutation({
    mutationFn: (values: z.infer<typeof outputSchema>) => projectLogframeApi.createOutput(outcome.id, values),
    onSuccess: () => { showSuccess('Output added.'); queryClient.invalidateQueries({ queryKey: ['project-outputs', outcome.id] }); setOutputModalOpen(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add output.'),
  });

  const editForm = useForm<z.infer<typeof outcomeSchema>>({ resolver: zodResolver(outcomeSchema), values: { statement: outcome.statement } });
  const editOutcomeMutation = useMutation({
    mutationFn: (values: z.infer<typeof outcomeSchema>) => projectLogframeApi.updateOutcome(outcome.id, values),
    onSuccess: () => { showSuccess('Outcome updated.'); queryClient.invalidateQueries({ queryKey: ['project-outcomes', projectId] }); setEditModalOpen(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update outcome.'),
  });

  const deleteOutputMutation = useMutation({
    mutationFn: (id: number) => projectLogframeApi.deactivateOutput(id),
    onSuccess: () => { showSuccess('Output deleted.'); queryClient.invalidateQueries({ queryKey: ['project-outputs', outcome.id] }); },
    onError: (err) => showError(deleteErrorMessage(err)),
  });

  function handleDeleteOutput(id: number, label: string) {
    if (window.confirm(`Are you sure you want to delete ${label}? This action cannot be undone.`)) {
      deleteOutputMutation.mutate(id);
    }
  }

  return (
    <section className={`card logframe-outcome ${OUTCOME_ACCENTS[colorIndex % OUTCOME_ACCENTS.length]}`}>
      <div className="page-header">
        <h3><span className="code-badge">Outcome {outcome.outcome_code}</span> {outcome.statement}</h3>
        <div className="output-actions">
          {canManage && <button className="btn-accent-2" onClick={() => setOutputModalOpen(true)}>+ Add Output</button>}
          {canManage && <button className="btn-secondary-text" onClick={() => setEditModalOpen(true)}>Edit</button>}
          {canManage && <button className="btn-danger-text" onClick={onDelete}>Delete</button>}
        </div>
      </div>

      <CommentThread projectId={projectId} entityType="outcome" entityId={outcome.id} />

      {outputsQuery.data && outputsQuery.data.data.length === 0 && <p className="muted">No outputs under this outcome yet.</p>}

      {outputsQuery.data?.data.map((output) => (
        <OutputBlock key={output.id} output={output} projectId={projectId} canManage={canManage} onDelete={handleDeleteOutput} />
      ))}

      {outputModalOpen && (
        <Modal title={`Add Output under Outcome ${outcome.outcome_code}`} onClose={() => setOutputModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createOutputMutation.mutate(values))}>
            <label>Output Statement<textarea rows={2} {...register('statement')} />{errors.statement && <span className="field-error">{errors.statement.message}</span>}</label>
            <p className="muted">The output number is assigned automatically.</p>
            <div className="form-actions"><button type="submit" disabled={createOutputMutation.isPending}>{createOutputMutation.isPending ? 'Saving…' : 'Add'}</button></div>
          </form>
        </Modal>
      )}

      {editModalOpen && (
        <Modal title={`Edit Outcome ${outcome.outcome_code}`} onClose={() => setEditModalOpen(false)}>
          <form onSubmit={editForm.handleSubmit((values) => editOutcomeMutation.mutate(values))}>
            <label>Outcome Statement<textarea rows={2} {...editForm.register('statement')} />{editForm.formState.errors.statement && <span className="field-error">{editForm.formState.errors.statement.message}</span>}</label>
            <div className="form-actions"><button type="submit" disabled={editOutcomeMutation.isPending}>{editOutcomeMutation.isPending ? 'Saving…' : 'Save Changes'}</button></div>
          </form>
        </Modal>
      )}
    </section>
  );
}

function OutputBlock({
  output, projectId, canManage, onDelete,
}: {
  output: { id: number; output_code: string; statement: string };
  projectId: number;
  canManage: boolean;
  onDelete: (id: number, label: string) => void;
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManageActivities = usePermission('project_workplan.manage');
  const [indicatorModalOpen, setIndicatorModalOpen] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editOutputModalOpen, setEditOutputModalOpen] = useState(false);

  const indicatorsQuery = useQuery({ queryKey: ['project-indicators', output.id], queryFn: () => projectLogframeApi.listIndicators(output.id) });
  const activitiesQuery = useQuery({ queryKey: ['project-output-activities', output.id], queryFn: () => projectActivitiesApi.listForOutput(output.id) });

  const editOutputForm = useForm<z.infer<typeof outputSchema>>({ resolver: zodResolver(outputSchema), values: { statement: output.statement } });
  const editOutputMutation = useMutation({
    mutationFn: (values: z.infer<typeof outputSchema>) => projectLogframeApi.updateOutput(output.id, values),
    onSuccess: () => { showSuccess('Output updated.'); queryClient.invalidateQueries({ queryKey: ['project-outputs'] }); setEditOutputModalOpen(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update output.'),
  });

  const indicatorForm = useForm<z.infer<typeof indicatorSchema>>({ resolver: zodResolver(indicatorSchema) });
  const createIndicatorMutation = useMutation({
    mutationFn: (values: z.infer<typeof indicatorSchema>) =>
      projectLogframeApi.createIndicator(output.id, {
        ...values,
        targetMen: toNullableNumber(values.targetMen),
        targetWomen: toNullableNumber(values.targetWomen),
        targetBoys: toNullableNumber(values.targetBoys),
        targetGirls: toNullableNumber(values.targetGirls),
        targetTotal: toNullableNumber(values.targetTotal),
      }),
    onSuccess: () => {
      showSuccess('Indicator added.');
      queryClient.invalidateQueries({ queryKey: ['project-indicators', output.id] });
      setIndicatorModalOpen(false);
      indicatorForm.reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add indicator.'),
  });

  const deleteIndicatorMutation = useMutation({
    mutationFn: (id: number) => projectLogframeApi.deactivateIndicator(id),
    onSuccess: () => { showSuccess('Indicator deleted.'); queryClient.invalidateQueries({ queryKey: ['project-indicators', output.id] }); },
    onError: (err) => showError(deleteErrorMessage(err)),
  });

  const activityForm = useForm<z.infer<typeof activitySchema>>({ resolver: zodResolver(activitySchema) });
  const createActivityMutation = useMutation({
    mutationFn: (values: z.infer<typeof activitySchema>) => projectActivitiesApi.createForOutput(output.id, values),
    onSuccess: () => {
      showSuccess('Activity added.');
      queryClient.invalidateQueries({ queryKey: ['project-output-activities', output.id] });
      queryClient.invalidateQueries({ queryKey: ['project-activities'] });
      setActivityModalOpen(false);
      activityForm.reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add activity.'),
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (id: number) => projectActivitiesApi.deactivate(id),
    onSuccess: () => {
      showSuccess('Activity deleted.');
      queryClient.invalidateQueries({ queryKey: ['project-output-activities', output.id] });
      queryClient.invalidateQueries({ queryKey: ['project-activities'] });
    },
    onError: (err) => showError(deleteErrorMessage(err)),
  });

  function handleDeleteActivity(id: number, label: string) {
    if (window.confirm(`Are you sure you want to delete ${label}? This action cannot be undone.`)) {
      deleteActivityMutation.mutate(id);
    }
  }

  return (
    <div className="logframe-output">
      <div className="page-header">
        <h4><span className="code-badge code-badge-output">Output {output.output_code}</span> {output.statement}</h4>
        <div className="output-actions">
          {canManageActivities && <button className="btn-accent-3" onClick={() => setActivityModalOpen(true)}>+ Add Activity</button>}
          {canManage && <button className="btn-accent-4" onClick={() => setIndicatorModalOpen(true)}>+ Add Indicator</button>}
          {canManage && <button className="btn-secondary-text" onClick={() => setEditOutputModalOpen(true)}>Edit</button>}
          {canManage && <button className="btn-danger-text" onClick={() => onDelete(output.id, `Output ${output.output_code}`)}>Delete</button>}
        </div>
      </div>

      <CommentThread projectId={projectId} entityType="output" entityId={output.id} />

      {activitiesQuery.data && activitiesQuery.data.data.length > 0 && (
        <ul className="activity-list">
          {activitiesQuery.data.data.map((act) => (
            <ActivityRow key={act.id} activity={act} outputId={output.id} canManage={canManageActivities} onDelete={handleDeleteActivity} />
          ))}
        </ul>
      )}
      {activitiesQuery.data && activitiesQuery.data.data.length === 0 && <p className="muted">No activities under this output yet.</p>}

      {indicatorsQuery.data && indicatorsQuery.data.data.length === 0 && <p className="muted">No indicators yet.</p>}

      <ul className="indicator-list">
        {indicatorsQuery.data?.data.map((ind) => (
          <IndicatorRow
            key={ind.id}
            indicator={ind}
            outputId={output.id}
            projectId={projectId}
            canManage={canManage}
            onDelete={() => {
              if (window.confirm(`Are you sure you want to delete Indicator ${ind.indicator_code}? This action cannot be undone.`)) {
                deleteIndicatorMutation.mutate(ind.id);
              }
            }}
          />
        ))}
      </ul>

      {indicatorModalOpen && (
        <Modal title={`Add Indicator under Output ${output.output_code}`} onClose={() => setIndicatorModalOpen(false)}>
          <form onSubmit={indicatorForm.handleSubmit((values) => createIndicatorMutation.mutate(values))}>
            <label>Indicator Statement<input {...indicatorForm.register('name')} />{indicatorForm.formState.errors.name && <span className="field-error">{indicatorForm.formState.errors.name.message}</span>}</label>
            <p className="muted">The indicator number is assigned automatically.</p>
            <label>Baseline<input {...indicatorForm.register('baselineValue')} /></label>
            <label>Target (narrative, optional)<input {...indicatorForm.register('targetValue')} /></label>

            <p className="muted target-breakdown-label">Target - segregated</p>
            <div className="target-input-grid">
              <label>Men<input type="number" min={0} {...indicatorForm.register('targetMen')} /></label>
              <label>Women<input type="number" min={0} {...indicatorForm.register('targetWomen')} /></label>
              <label>Boys<input type="number" min={0} {...indicatorForm.register('targetBoys')} /></label>
              <label>Girls<input type="number" min={0} {...indicatorForm.register('targetGirls')} /></label>
              <label>Total<input type="number" min={0} {...indicatorForm.register('targetTotal')} /></label>
            </div>

            <label>Unit of Measurement<input {...indicatorForm.register('unitOfMeasurement')} /></label>
            <label>Means of Verification<textarea rows={2} placeholder="e.g. Kobo/ODK data, attendance sheets, site verification reports" {...indicatorForm.register('meansOfVerification')} /></label>
            <div className="form-actions"><button type="submit" disabled={createIndicatorMutation.isPending}>{createIndicatorMutation.isPending ? 'Saving…' : 'Add'}</button></div>
          </form>
        </Modal>
      )}

      {activityModalOpen && (
        <Modal title={`Add Activity under Output ${output.output_code}`} onClose={() => setActivityModalOpen(false)}>
          <form onSubmit={activityForm.handleSubmit((values) => createActivityMutation.mutate(values))}>
            <label>Activity Description<input {...activityForm.register('name')} />{activityForm.formState.errors.name && <span className="field-error">{activityForm.formState.errors.name.message}</span>}</label>
            <p className="muted">The activity number is assigned automatically.</p>
            <div className="form-actions"><button type="submit" disabled={createActivityMutation.isPending}>{createActivityMutation.isPending ? 'Saving…' : 'Add'}</button></div>
          </form>
        </Modal>
      )}

      {editOutputModalOpen && (
        <Modal title={`Edit Output ${output.output_code}`} onClose={() => setEditOutputModalOpen(false)}>
          <form onSubmit={editOutputForm.handleSubmit((values) => editOutputMutation.mutate(values))}>
            <label>Output Statement<textarea rows={2} {...editOutputForm.register('statement')} />{editOutputForm.formState.errors.statement && <span className="field-error">{editOutputForm.formState.errors.statement.message}</span>}</label>
            <div className="form-actions"><button type="submit" disabled={editOutputMutation.isPending}>{editOutputMutation.isPending ? 'Saving…' : 'Save Changes'}</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}

/** Full indicator edit form, pre-filled from the existing record. */
function IndicatorRow({
  indicator, outputId, projectId, canManage, onDelete,
}: {
  indicator: ProjectIndicator;
  outputId: number;
  projectId: number;
  canManage: boolean;
  onDelete: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const editForm = useForm<z.infer<typeof indicatorSchema>>({
    resolver: zodResolver(indicatorSchema),
    values: {
      name: indicator.name,
      baselineValue: indicator.baseline_value ?? '',
      targetValue: indicator.target_value ?? '',
      targetMen: indicator.target_men?.toString() ?? '',
      targetWomen: indicator.target_women?.toString() ?? '',
      targetBoys: indicator.target_boys?.toString() ?? '',
      targetGirls: indicator.target_girls?.toString() ?? '',
      targetTotal: indicator.target_total?.toString() ?? '',
      unitOfMeasurement: indicator.unit_of_measurement ?? '',
      meansOfVerification: indicator.means_of_verification ?? '',
    },
  });

  const editMutation = useMutation({
    mutationFn: (values: z.infer<typeof indicatorSchema>) =>
      projectLogframeApi.updateIndicator(indicator.id, {
        ...values,
        targetMen: toNullableNumber(values.targetMen),
        targetWomen: toNullableNumber(values.targetWomen),
        targetBoys: toNullableNumber(values.targetBoys),
        targetGirls: toNullableNumber(values.targetGirls),
        targetTotal: toNullableNumber(values.targetTotal),
      }),
    onSuccess: () => { showSuccess('Indicator updated.'); queryClient.invalidateQueries({ queryKey: ['project-indicators', outputId] }); setEditModalOpen(false); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update indicator.'),
  });

  const hasSegregated = [indicator.target_men, indicator.target_women, indicator.target_boys, indicator.target_girls, indicator.target_total].some((v) => v !== null && v !== undefined);

  return (
    <li>
      <div className="indicator-row-header">
        <span>
          <span className="code-badge code-badge-indicator">Indicator {indicator.indicator_code}</span> {indicator.name}
          {(indicator.baseline_value || indicator.target_value) && (
            <span className="muted"> - Baseline: {indicator.baseline_value ?? '-'} · Target: {indicator.target_value ?? '-'}{indicator.unit_of_measurement ? ` ${indicator.unit_of_measurement}` : ''}</span>
          )}
          {indicator.means_of_verification && <div className="muted indicator-mov">MOV: {indicator.means_of_verification}</div>}
        </span>
        {canManage && (
          <span className="row-actions">
            <button className="btn-secondary-text" onClick={() => setEditModalOpen(true)}>Edit</button>
            <button className="btn-danger-text" onClick={onDelete}>Delete</button>
          </span>
        )}
      </div>
      {hasSegregated && (
        <div className="indicator-target-breakdown">
          {indicator.target_men !== null && <span className="target-chip target-chip-men">Men: {indicator.target_men}</span>}
          {indicator.target_women !== null && <span className="target-chip target-chip-women">Women: {indicator.target_women}</span>}
          {indicator.target_boys !== null && <span className="target-chip target-chip-boys">Boys: {indicator.target_boys}</span>}
          {indicator.target_girls !== null && <span className="target-chip target-chip-girls">Girls: {indicator.target_girls}</span>}
          {indicator.target_total !== null && <span className="target-chip target-chip-total">Total: {indicator.target_total}</span>}
        </div>
      )}
      <CommentThread projectId={projectId} entityType="indicator" entityId={indicator.id} />

      {editModalOpen && (
        <Modal title={`Edit Indicator ${indicator.indicator_code}`} onClose={() => setEditModalOpen(false)}>
          <form onSubmit={editForm.handleSubmit((values) => editMutation.mutate(values))}>
            <label>Indicator Statement<input {...editForm.register('name')} />{editForm.formState.errors.name && <span className="field-error">{editForm.formState.errors.name.message}</span>}</label>
            <label>Baseline<input {...editForm.register('baselineValue')} /></label>
            <label>Target (narrative, optional)<input {...editForm.register('targetValue')} /></label>

            <p className="muted target-breakdown-label">Target - segregated</p>
            <div className="target-input-grid">
              <label>Men<input type="number" min={0} {...editForm.register('targetMen')} /></label>
              <label>Women<input type="number" min={0} {...editForm.register('targetWomen')} /></label>
              <label>Boys<input type="number" min={0} {...editForm.register('targetBoys')} /></label>
              <label>Girls<input type="number" min={0} {...editForm.register('targetGirls')} /></label>
              <label>Total<input type="number" min={0} {...editForm.register('targetTotal')} /></label>
            </div>

            <label>Unit of Measurement<input {...editForm.register('unitOfMeasurement')} /></label>
            <label>Means of Verification<textarea rows={2} {...editForm.register('meansOfVerification')} /></label>
            <div className="form-actions"><button type="submit" disabled={editMutation.isPending}>{editMutation.isPending ? 'Saving…' : 'Save Changes'}</button></div>
          </form>
        </Modal>
      )}
    </li>
  );
}

const activityEditSchema = z.object({ name: z.string().min(3) });

/**
 * Activity edit only exposes the name field in this modal, but the
 * update endpoint replaces every field on the record - so the
 * mutation sends the activity's existing status/progress/planned
 * months/etc back unchanged alongside the new name, rather than
 * letting the endpoint's schema defaults silently wipe out ticks
 * already made on the Work Plan tab's Gantt grid.
 */
function ActivityRow({
  activity, outputId, canManage, onDelete,
}: {
  activity: ProjectActivity;
  outputId: number;
  canManage: boolean;
  onDelete: (id: number, label: string) => void;
}) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const editForm = useForm<z.infer<typeof activityEditSchema>>({ resolver: zodResolver(activityEditSchema), values: { name: activity.name } });

  const editMutation = useMutation({
    mutationFn: (values: z.infer<typeof activityEditSchema>) =>
      projectActivitiesApi.update(activity.id, {
        name: values.name,
        responsibleUserId: activity.responsible_user_id,
        locationId: activity.location_id,
        plannedStartDate: activity.planned_start_date,
        plannedEndDate: activity.planned_end_date,
        plannedMonths: activity.planned_months,
        status: activity.status,
        progressPercent: activity.progress_percent,
        notes: activity.notes,
      }),
    onSuccess: () => {
      showSuccess('Activity updated.');
      queryClient.invalidateQueries({ queryKey: ['project-output-activities', outputId] });
      queryClient.invalidateQueries({ queryKey: ['project-activities'] });
      setEditModalOpen(false);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to update activity.'),
  });

  return (
    <li>
      <span className="code-badge code-badge-activity">Activity {activity.activity_code}</span> {activity.name}
      {canManage && (
        <span className="row-actions">
          <button className="btn-secondary-text activity-delete-btn" onClick={() => setEditModalOpen(true)}>Edit</button>
          <button className="btn-danger-text activity-delete-btn" onClick={() => onDelete(activity.id, `Activity ${activity.activity_code}`)}>Delete</button>
        </span>
      )}

      {editModalOpen && (
        <Modal title={`Edit Activity ${activity.activity_code}`} onClose={() => setEditModalOpen(false)}>
          <form onSubmit={editForm.handleSubmit((values) => editMutation.mutate(values))}>
            <label>Activity Description<input {...editForm.register('name')} />{editForm.formState.errors.name && <span className="field-error">{editForm.formState.errors.name.message}</span>}</label>
            <div className="form-actions"><button type="submit" disabled={editMutation.isPending}>{editMutation.isPending ? 'Saving…' : 'Save Changes'}</button></div>
          </form>
        </Modal>
      )}
    </li>
  );
}
